import { useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { ADMIN_PRODUCTS_API_PATHS } from '../constants/products-api-paths.constants';
import { useUpdateProduct } from './update-product.hook';

type UseUploadProductImageResult = {
  uploadProductImage: (productId: string, file: File) => Promise<string | null>;
  isSubmitting: boolean;
};

const UPLOAD_ERROR_MESSAGE = "Une erreur est survenue lors du téléversement de l'image";

export function useUploadProductImage(): UseUploadProductImageResult {
  const { enqueueSnackbar } = useSnackbar();
  const { updateProduct } = useUpdateProduct();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const uploadProductImage = async (productId: string, file: File): Promise<string | null> => {
    setIsSubmitting(true);

    try {
      const { url, key } = await HttpClient.get<{ url: string; key: string }>(ADMIN_PRODUCTS_API_PATHS.GET_UPLOAD_URL, {
        params: { productId, contentType: file.type },
      });

      const uploadResponse = await fetch(url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!uploadResponse.ok) {
        enqueueSnackbar(UPLOAD_ERROR_MESSAGE, { variant: 'error' });
        return null;
      }

      await updateProduct({ productId, productData: { photo: key } });

      return key;
    } catch (error) {
      enqueueSnackbar(extractTranslatedApiErrorMessage(error, UPLOAD_ERROR_MESSAGE), { variant: 'error' });
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  return { uploadProductImage, isSubmitting };
}
